import React, {useMemo} from 'react';
import PaginationNumber from '../../atoms/Links/Pagination/Number/PaginationNumber';
import PaginationArrow from '../Links/Pagination/Arrow/PaginationArrow';
import StyledPagination from './styles';
import Props, {DotsType} from './typings';

const DOTS: Readonly<DotsType> = '...';

const Pagination = ({
  activePage,
  totalCount,
  paginate,
  onClick,
  siblingCount = 1,
  position = 'right',
  basePath = '/',
  urlParams = '',
}: Props) => {
  const paginationRange: (number | DotsType)[] = usePagination({
    activePage,
    totalCount,
    paginate,
    siblingCount,
  });
  const lastPage = paginationRange[paginationRange.length - 1];

  const prevClick = () => {
    onClick(activePage - 1);
  };

  const nextClick = () => {
    onClick(activePage + 1);
  };

  return (
    <StyledPagination $position={position}>
      <PaginationArrow
        left
        href={
          activePage === paginationRange[0]
            ? undefined
            : `${basePath}${urlParams && `${urlParams}/`}${
                activePage - 1 === 1 ? '' : `page-${activePage - 1}/`
              }`
        }
        onClick={prevClick}
        disabled={activePage === paginationRange[0]}
      />

      {paginationRange.map((page, index) => (
        <PaginationNumber
          key={index}
          href={
            page === DOTS
              ? '#'
              : `${basePath}${urlParams && `${urlParams}/`}${page === 1 ? '' : `page-${page}/`}`
          }
          number={page}
          active={page === activePage}
          onClick={onClick}
          disabled={page === DOTS}
        />
      ))}

      <PaginationArrow
        right
        href={
          activePage === lastPage
            ? undefined
            : `${basePath}${urlParams && `${urlParams}/`}page-${activePage + 1}/`
        }
        onClick={nextClick}
        disabled={activePage === lastPage}
      />
    </StyledPagination>
  );
};

export default Pagination;

const usePagination = ({activePage, totalCount, paginate, siblingCount}): (number | DotsType)[] => {
  const range = (start, end) => {
    const length = end - start + 1;
    return Array.from({length}, (_, index) => index + start);
  };

  const paginationRange = useMemo((): (number | DotsType)[] => {
    const totalPageCount = Math.ceil(totalCount / paginate);
    const totalPageNumbers = siblingCount + 5;

    if (totalPageNumbers >= totalPageCount) {
      return range(1, totalPageCount);
    }

    const leftSiblingIndex = Math.max(activePage - siblingCount, 1);
    const rightSiblingIndex = Math.min(activePage + siblingCount, totalPageCount);

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

    const firstPageNumber = 1;
    // const lastPageNumber = totalPageCount;

    // show only right dots
    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 3 + 2 * siblingCount;
      const leftRange = range(1, leftItemCount);

      return [...leftRange, DOTS];
    }

    // show only left dots
    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + 2 * siblingCount;
      const rightRange = range(totalPageCount - rightItemCount + 1, totalPageCount);
      return [firstPageNumber, DOTS, ...rightRange];
    }

    // show both dots
    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [firstPageNumber, DOTS, ...middleRange, DOTS];
    }

    // Default return
    return [];
  }, [totalCount, paginate, siblingCount, activePage]);

  return paginationRange;
};
